// Level entities: pickups, platforms, checkpoints, brain gates, lucky blocks, portal, chest, NPCs, signs.
import * as THREE from 'three';
import { B, BLOCKS } from './blocks.js';
import { blockGeometry, meshBlockList } from './voxel.js';
import { getCoinAssets, gemModel, starModel, heartModel, powerupModel, atlasBox, getAtlasMaterial } from './models.js';
import { Character } from './character.js';
import { paintNPC } from './skins.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { easeOutBack, easeOutCubic, lerp, escapeHtml, withName } from './util.js';

const _v = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _e = new THREE.Euler();

export function disposeObj(o) {
  o.traverse((c) => {
    if (c.geometry && !c.geometry.userData.shared) c.geometry.dispose();
    if (c.material) {
      const mats = Array.isArray(c.material) ? c.material : [c.material];
      for (const m of mats) if (!m.userData.shared) m.dispose();
    }
  });
}

class Ent {
  constructor(level) {
    this.level = level;
    this.dead = false;
  }
  add(o) {
    this.level.group.add(o);
    return o;
  }
  update() {}
  dispose() {
    if (this.obj) {
      this.level.group.remove(this.obj);
      disposeObj(this.obj);
    }
  }
}

// ------------------------------------------------------------------ coins
export class CoinField extends Ent {
  constructor(level, list) {
    super(level);
    const { geo, mats } = getCoinAssets();
    geo.userData.shared = true;
    mats.forEach((m) => (m.userData.shared = true));
    this.n = list.length;
    this.p = list.map((c) => new THREE.Vector3(c.x, c.y, c.z));
    this.alive = new Uint8Array(this.n).fill(1);
    this.pull = new Float32Array(this.n);
    this.mesh = new THREE.InstancedMesh(geo, mats, Math.max(1, this.n));
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.count = this.n;
    this.obj = this.add(this.mesh);
    this.collected = 0;
  }
  update(dt, t) {
    const L = this.level, pl = L.player;
    const cx = pl.pos.x, cy = pl.pos.y + 0.9, cz = pl.pos.z;
    const mag = L.magnetRadius();
    const mag2 = mag * mag;
    for (let i = 0; i < this.n; i++) {
      if (!this.alive[i]) continue;
      const p = this.p[i];
      const dx = cx - p.x, dy = cy - p.y, dz = cz - p.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < 0.95) {
        this.alive[i] = 0;
        _m.makeScale(0, 0, 0);
        this.mesh.setMatrixAt(i, _m);
        this.collected++;
        L.onCoin(p, 1);
        continue;
      }
      if (d2 < mag2 || this.pull[i] > 0) {
        this.pull[i] = Math.min(30, this.pull[i] + dt * 40);
        const d = Math.sqrt(d2);
        const sp = Math.min(d, (4 + this.pull[i]) * dt);
        p.x += (dx / d) * sp;
        p.y += (dy / d) * sp;
        p.z += (dz / d) * sp;
      }
      const bob = Math.sin(t * 3 + i * 0.7) * 0.08;
      _e.set(0, t * 3.2 + i * 0.4, 0);
      _q.setFromEuler(_e);
      _v.set(p.x, p.y + bob, p.z);
      _s.set(1, 1, 1);
      _m.compose(_v, _q, _s);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
  dispose() {
    this.level.group.remove(this.mesh);
    this.mesh.dispose();
  }
}

// ------------------------------------------------------------------ loot (burst of coins/gems that home in)
export class Loot extends Ent {
  constructor(level, pos, kind = 'coin', vel = null) {
    super(level);
    this.kind = kind;
    if (kind === 'coin') {
      const { geo, mats } = getCoinAssets();
      this.obj = new THREE.Mesh(geo, mats);
      this.obj.scale.setScalar(0.7);
    } else {
      this.obj = gemModel(kind === 'ruby' ? '#ff4d6d' : '#4ff0ff');
      this.obj.scale.setScalar(0.7);
    }
    this.obj.position.copy(pos);
    this.add(this.obj);
    const a = Math.random() * Math.PI * 2;
    this.v = vel || new THREE.Vector3(Math.cos(a) * (2 + Math.random() * 3), 6 + Math.random() * 4, Math.sin(a) * (2 + Math.random() * 3));
    this.t = 0;
  }
  update(dt) {
    this.t += dt;
    const o = this.obj, pl = this.level.player;
    o.rotation.y += dt * 8;
    if (this.t < 0.45) {
      this.v.y -= 22 * dt;
      o.position.addScaledVector(this.v, dt);
    } else {
      _v.set(pl.pos.x, pl.pos.y + 0.9, pl.pos.z).sub(o.position);
      const d = _v.length();
      const sp = Math.min(d, (8 + this.t * 26) * dt);
      o.position.addScaledVector(_v.normalize(), sp);
      if (d < 0.6) {
        this.dead = true;
        if (this.kind === 'coin') this.level.onCoin(o.position, 1);
        else this.level.onGem(o.position, 1);
      }
    }
  }
  dispose() {
    this.level.group.remove(this.obj);
    if (this.kind !== 'coin') disposeObj(this.obj);
  }
}

// ------------------------------------------------------------------ pickups: gem, star, heart, powerup
export class Pickup extends Ent {
  constructor(level, kind, x, y, z, extra = {}) {
    super(level);
    this.kind = kind;
    this.extra = extra;
    let o;
    if (kind === 'gem') o = gemModel('#4ff0ff');
    else if (kind === 'star') o = starModel('#ffd23f', 0.55);
    else if (kind === 'heart') o = heartModel();
    else o = powerupModel(extra.power);
    const g = new THREE.Group();
    g.add(o);
    g.position.set(x, y, z);
    this.base = y;
    this.obj = this.add(g);
    this.inner = o;
    this.t = Math.random() * 6;
    this.radius = kind === 'star' ? 1.2 : 1.0;
    if (kind === 'star') {
      // beacon beam so kids can spot the secret star from afar
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.4, 14, 8, 1, true), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 1.3, 0.4), transparent: true, opacity: 0.22, depthWrite: false, side: THREE.DoubleSide }));
      beam.position.y = 7;
      g.add(beam);
      this.beam = beam;
    }
  }
  update(dt) {
    this.t += dt;
    const o = this.obj;
    o.position.y = this.base + Math.sin(this.t * 2.4) * 0.15;
    this.inner.rotation.y += dt * (this.kind === 'star' ? 2.2 : 2.8);
    if (this.beam) this.beam.material.opacity = 0.16 + Math.sin(this.t * 3) * 0.06;
    if (Math.random() < dt * (this.kind === 'star' ? 10 : 3)) this.level.fx.particles.sparkle(o.position, this.kind === 'gem' ? '#9ffcff' : this.kind === 'heart' ? '#ff9ab0' : '#fff0a0', 1);
    const pl = this.level.player;
    _v.set(pl.pos.x, pl.pos.y + 0.9, pl.pos.z);
    if (_v.distanceTo(o.position) < this.radius) {
      this.dead = true;
      this.level.onPickup(this.kind, o.position.clone(), this.extra);
    }
  }
}

// ------------------------------------------------------------------ moving platform
export class MovingPlatform extends Ent {
  constructor(level, spec) {
    super(level);
    const list = [];
    for (let x = 0; x < spec.w; x++) for (let z = 0; z < spec.d; z++) list.push({ x, y: 0, z, id: spec.block });
    const geo = meshBlockList(list).solid;
    const m = new THREE.Mesh(geo, level.mats.solid);
    m.castShadow = true;
    m.receiveShadow = true;
    this.obj = this.add(m);
    this.from = new THREE.Vector3(spec.from.x, spec.from.y, spec.from.z);
    this.to = new THREE.Vector3(spec.to.x, spec.to.y, spec.to.z);
    this.period = spec.period || 4;
    this.phase = spec.phase || 0;
    this.size = new THREE.Vector3(spec.w, 1, spec.d);
    this.pos = this.from.clone();
    this.col = { min: new THREE.Vector3(), max: new THREE.Vector3(), solid: true, delta: new THREE.Vector3(), blockId: spec.block, moving: true };
    level.colliders.push(this.col);
    this.t = this.phase * this.period;
    this.place();
  }
  place() {
    this.col.min.copy(this.pos);
    this.col.max.copy(this.pos).add(this.size);
    this.obj.position.copy(this.pos);
  }
  update(dt) {
    this.t += dt;
    const s = (1 - Math.cos((this.t / this.period) * Math.PI * 2)) / 2;
    const prev = _v.copy(this.pos);
    this.pos.lerpVectors(this.from, this.to, s);
    this.col.delta.subVectors(this.pos, prev);
    this.place();
  }
  dispose() {
    super.dispose();
    const i = this.level.colliders.indexOf(this.col);
    if (i >= 0) this.level.colliders.splice(i, 1);
  }
}

// ------------------------------------------------------------------ crumbling block
export class CrumbleBlock extends Ent {
  constructor(level, spec) {
    super(level);
    const m = new THREE.Mesh(blockGeometry(spec.block), level.mats.solid);
    m.castShadow = true;
    m.receiveShadow = true;
    this.home = new THREE.Vector3(spec.x + 0.5, spec.y + 0.5, spec.z + 0.5);
    m.position.copy(this.home);
    this.obj = this.add(m);
    this.col = { min: new THREE.Vector3(spec.x, spec.y, spec.z), max: new THREE.Vector3(spec.x + 1, spec.y + 1, spec.z + 1), solid: true, blockId: spec.block };
    level.colliders.push(this.col);
    this.state = 'idle';
    this.t = 0;
    this.block = spec.block;
    this.vy = 0;
  }
  update(dt) {
    const pl = this.level.player;
    if (this.state === 'idle') {
      if (pl.platform === this.col) {
        this.state = 'shake';
        this.t = 0;
        audio.play('crumble');
      }
    } else if (this.state === 'shake') {
      this.t += dt;
      this.obj.position.set(this.home.x + (Math.random() - 0.5) * 0.08, this.home.y + (Math.random() - 0.5) * 0.05, this.home.z + (Math.random() - 0.5) * 0.08);
      if (this.t > 0.55) {
        this.state = 'fall';
        this.col.solid = false;
        this.t = 0;
        this.vy = 0;
        this.level.fx.particles.burst(this.home, { count: 6, colors: [BLOCKS[this.block].color], size: 0.12, speed: 2, up: 1 });
      }
    } else if (this.state === 'fall') {
      this.t += dt;
      this.vy -= 25 * dt;
      this.obj.position.y += this.vy * dt;
      this.obj.rotation.x += dt * 2;
      if (this.t > 1.2) {
        this.obj.visible = false;
        this.state = 'gone';
        this.t = 0;
      }
    } else if (this.state === 'gone') {
      this.t += dt;
      if (this.t > 2.6) {
        // don't respawn inside the player
        const p = pl.pos;
        if (p.x + 0.3 > this.col.min.x && p.x - 0.3 < this.col.max.x && p.z + 0.3 > this.col.min.z && p.z - 0.3 < this.col.max.z && p.y < this.col.max.y + 0.2 && p.y + 1.8 > this.col.min.y) return;
        this.state = 'idle';
        this.col.solid = true;
        this.obj.visible = true;
        this.obj.rotation.set(0, 0, 0);
        this.obj.position.copy(this.home);
        this.obj.scale.setScalar(0.01);
        this.grow = 0;
      }
    }
    if (this.grow !== undefined && this.grow < 1) {
      this.grow = Math.min(1, this.grow + dt * 4);
      this.obj.scale.setScalar(easeOutBack(this.grow));
    }
  }
  dispose() {
    this.level.group.remove(this.obj);
    const i = this.level.colliders.indexOf(this.col);
    if (i >= 0) this.level.colliders.splice(i, 1);
  }
}

// ------------------------------------------------------------------ checkpoint
function flagTexture(active) {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 80;
  const ctx = c.getContext('2d');
  ctx.fillStyle = active ? '#37c84a' : '#8a92a0';
  ctx.fillRect(0, 0, 128, 80);
  ctx.fillStyle = active ? '#2a9a38' : '#6a7280';
  for (let i = 0; i < 128; i += 16) ctx.fillRect(i, 0, 8, 80);
  ctx.fillStyle = '#ffffff';
  ctx.font = '56px "Lilita One", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 8;
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.strokeText(active ? '✓' : 'S', 64, 44);
  ctx.fillText(active ? '✓' : 'S', 64, 44);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class Checkpoint extends Ent {
  constructor(level, spec) {
    super(level);
    this.pos = new THREE.Vector3(spec.x + 0.5, spec.y, spec.z + 0.5);
    this.start = !!spec.start;
    level.world.set(spec.x, spec.y - 1, spec.z, B.CHECKPOINT);
    const g = new THREE.Group();
    g.position.set(spec.x + 1.35, spec.y, spec.z + 0.5);
    const pole = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.6, 0.1), new THREE.MeshLambertMaterial({ color: '#e8e8ee' }));
    pole.position.y = 1.3;
    pole.castShadow = true;
    const knob = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), new THREE.MeshLambertMaterial({ color: '#ffd23f', emissive: '#ffb000', emissiveIntensity: 0.6 }));
    knob.position.y = 2.65;
    this.flagGeo = new THREE.PlaneGeometry(0.95, 0.6, 8, 1);
    this.flagGeo.translate(0.5, 0, 0);
    this.flagBase = this.flagGeo.attributes.position.array.slice();
    this.flagMat = new THREE.MeshLambertMaterial({ map: flagTexture(false), side: THREE.DoubleSide });
    this.flag = new THREE.Mesh(this.flagGeo, this.flagMat);
    this.flag.position.set(0.05, 1.0, 0);
    this.flag.castShadow = true;
    g.add(pole, knob, this.flag);
    this.obj = this.add(g);
    this.active = false;
    this.rise = 0;
    this.t = Math.random() * 5;
  }
  activate(silent = false) {
    if (this.active) return;
    this.active = true;
    this.flagMat.map.dispose();
    this.flagMat.map = flagTexture(true);
    this.flagMat.emissive = new THREE.Color('#1a6a20');
    this.flagMat.needsUpdate = true;
    this.level.setCheckpoint(this);
    if (!silent) {
      audio.play('checkpoint');
      haptics.play('checkpoint');
      this.level.fx.particles.burst(this.pos.clone().add(new THREE.Vector3(0, 0.5, 0)), { count: 26, colors: ['#7be35a', '#ffffff', '#ffd23f'], speed: 4, up: 6, glow: true, bright: 1.6, size: 0.12 });
      this.level.fx.rings.add(this.pos, { color: '#7be35a', to: 3.5 });
      this.level.fx.worldUI.float('CHECKPOINT!', this.pos.clone().add(new THREE.Vector3(0, 2.4, 0)), 'ft-green ft-big');
    }
  }
  update(dt) {
    this.t += dt;
    if (this.active && this.rise < 1) {
      this.rise = Math.min(1, this.rise + dt * 1.8);
      this.flag.position.y = 1.0 + easeOutCubic(this.rise) * 1.3;
    }
    // wave the flag
    const a = this.flagGeo.attributes.position;
    for (let i = 0; i < a.count; i++) {
      const x = this.flagBase[i * 3];
      a.setZ(i, Math.sin(this.t * 6 + x * 5) * 0.08 * x);
    }
    a.needsUpdate = true;
    if (!this.active) {
      const p = this.level.player.pos;
      if (Math.abs(p.x - this.pos.x) < 1.6 && Math.abs(p.z - this.pos.z) < 1.6 && Math.abs(p.y - this.pos.y) < 2) this.activate();
    }
  }
}

// ------------------------------------------------------------------ brain gate
let ghostGeo = null;
export class BrainGate extends Ent {
  constructor(level, spec) {
    super(level);
    this.spec = spec;
    this.needed = spec.needed || 1;
    this.built = 0;
    this.solved = false;
    this.busy = false;
    this.trigger = new THREE.Vector3(spec.trigger.x, spec.trigger.y, spec.trigger.z);
    // sort blocks along the path so the bridge builds outwards
    this.blocks = spec.blocks.slice().sort((a, b) => a.z - b.z || a.x - b.x);
    const g = new THREE.Group();
    // floating brain block
    const bm = new THREE.Mesh(atlasBox(0.9, 0.9, 0.9, { top: 'brain', side: 'brain' }), getAtlasMaterial());
    bm.castShadow = true;
    this.brain = bm;
    bm.position.copy(this.trigger).add(new THREE.Vector3(0, 1.7, 0));
    g.add(bm);
    // ghost outline of the missing bridge
    if (!ghostGeo) {
      ghostGeo = new THREE.BoxGeometry(0.96, 0.96, 0.96);
      ghostGeo.userData.shared = true;
    }
    this.ghostMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0.4, 0.9, 1.4), transparent: true, opacity: 0.18, depthWrite: false });
    this.ghost = new THREE.InstancedMesh(ghostGeo, this.ghostMat, this.blocks.length);
    this.blocks.forEach((b, i) => {
      _m.makeTranslation(b.x + 0.5, b.y + 0.5, b.z + 0.5);
      this.ghost.setMatrixAt(i, _m);
    });
    this.ghost.frustumCulled = false;
    g.add(this.ghost);
    this.obj = this.add(g);
    this.flying = [];
    this.t = 0;
  }
  segment(k) {
    // blocks for the k-th correct answer (1-based)
    const n = this.blocks.length;
    const a = Math.floor(((k - 1) / this.needed) * n), b = Math.floor((k / this.needed) * n);
    return this.blocks.slice(a, b);
  }
  buildSegment(k) {
    const seg = this.segment(k);
    const L = this.level;
    seg.forEach((b, i) => {
      const m = new THREE.Mesh(blockGeometry(b.id), L.mats.solid);
      const start = new THREE.Vector3(b.x + 0.5 + (Math.random() - 0.5) * 6, b.y - 7 - Math.random() * 5, b.z + 0.5 + (Math.random() - 0.5) * 4);
      m.position.copy(start);
      m.castShadow = true;
      this.obj.add(m);
      this.flying.push({ m, b, start, t: -i * 0.05 - 0.15, idx: i });
    });
    this.built = k;
    this.updateGhost();
  }
  updateGhost() {
    const done = Math.floor((this.built / this.needed) * this.blocks.length);
    this.blocks.forEach((b, i) => {
      if (i < done) _m.makeScale(0, 0, 0);
      else _m.makeTranslation(b.x + 0.5, b.y + 0.5, b.z + 0.5);
      this.ghost.setMatrixAt(i, _m);
    });
    this.ghost.instanceMatrix.needsUpdate = true;
  }
  async interact() {
    if (this.busy || this.solved) return;
    this.busy = true;
    const L = this.level;
    const startBuilt = this.built;
    const res = await L.startQuiz({
      title: this.spec.title || 'Rebuild the bridge!',
      sub: this.needed > 1 ? `Answer <b>${this.needed - startBuilt}</b> question${this.needed - startBuilt > 1 ? 's' : ''} to rebuild it!` : 'Answer correctly to rebuild it!',
      needed: this.needed - startBuilt,
      allowLater: true,
      onCorrect: (k) => this.buildSegment(startBuilt + k),
    });
    this.busy = false;
    if (this.built >= this.needed) this.solve();
    void res;
  }
  solve() {
    if (this.solved) return;
    this.solved = true;
    const L = this.level;
    const p = this.brain.position.clone();
    L.fx.particles.confetti(p, 50, 1);
    L.fx.particles.burst(p, { count: 20, colors: ['#b070ff', '#ffffff'], speed: 5, up: 4, glow: true, bright: 1.8 });
    audio.play('unlock');
    this.brain.visible = false;
    this.ghost.visible = false;
    L.fx.worldUI.float('BRIDGE FIXED!', p, 'ft-gold ft-big', { life: 1.6 });
    L.onGateSolved(this);
  }
  update(dt) {
    this.t += dt;
    const L = this.level;
    if (!this.solved) {
      this.brain.rotation.y += dt * 1.4;
      this.brain.position.y = this.trigger.y + 1.7 + Math.sin(this.t * 2.5) * 0.15;
      this.ghostMat.opacity = 0.14 + Math.sin(this.t * 4) * 0.07;
      if (Math.random() < dt * 6) L.fx.particles.sparkle(this.brain.position, '#d0a0ff', 1);
      const p = L.player.pos;
      const d = Math.hypot(p.x - this.trigger.x, p.z - this.trigger.z);
      if (d < 3.2 && Math.abs(p.y - this.trigger.y) < 2.5 && !this.busy) {
        L.offerAction(this, this.built > 0 ? '⚡ Finish the bridge!' : '⚡ Fix the bridge!', 'SOLVE', '⚡', this.brain.position.clone().add(new THREE.Vector3(0, 0.9, 0)), () => this.interact());
      }
    }
    // animate flying blocks
    for (let i = this.flying.length - 1; i >= 0; i--) {
      const f = this.flying[i];
      f.t += dt;
      if (f.t < 0) continue;
      const k = Math.min(1, f.t / 0.55);
      const e = easeOutBack(k);
      f.m.position.set(lerp(f.start.x, f.b.x + 0.5, e), lerp(f.start.y, f.b.y + 0.5, e), lerp(f.start.z, f.b.z + 0.5, e));
      f.m.rotation.set((1 - k) * 6, (1 - k) * 4, 0);
      if (k >= 1) {
        L.world.set(f.b.x, f.b.y, f.b.z, f.b.id, true);
        L.worldDirty = true;
        this.obj.remove(f.m);
        this.flying.splice(i, 1);
        audio.play('build', { pitch: 0.8 + (f.idx % 12) * 0.06 });
        if (f.idx % 2 === 0) L.fx.particles.burst(new THREE.Vector3(f.b.x + 0.5, f.b.y + 1, f.b.z + 0.5), { count: 4, colors: ['#ffffff', '#d0f0ff'], size: 0.08, speed: 1.5, up: 2, glow: true });
      }
    }
  }
}

// ------------------------------------------------------------------ lucky / brain blocks
export class LuckyBlock extends Ent {
  constructor(level, x, y, z, brain = false) {
    super(level);
    this.brainBlock = brain;
    const m = new THREE.Mesh(atlasBox(1, 1, 1, { top: brain ? 'brain' : 'lucky', side: brain ? 'brain' : 'lucky' }), getAtlasMaterial());
    m.castShadow = true;
    m.receiveShadow = true;
    m.position.set(x, y, z);
    this.obj = this.add(m);
    this.center = new THREE.Vector3(x, y, z);
    this.col = { min: new THREE.Vector3(x - 0.5, y - 0.5, z - 0.5), max: new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5), solid: true, blockId: B.LUCKY };
    level.colliders.push(this.col);
    level.hittables.push(this);
    this.t = Math.random() * 4;
    this.busy = false;
  }
  hit() {
    if (this.dead || this.busy) return;
    if (this.brainBlock) return this.askBrain();
    this.breakOpen();
    this.level.rollLucky(this.center.clone());
  }
  breakOpen() {
    this.dead = true;
    const L = this.level;
    L.fx.particles.blockBreak(this.center, this.brainBlock ? B.CRYSTAL : B.LUCKY);
    L.fx.particles.burst(this.center, { count: 18, colors: this.brainBlock ? ['#c080ff', '#ffffff'] : ['#ffd23f', '#ffffff'], speed: 5, up: 6, glow: true, bright: 2, size: 0.1 });
    audio.play('break');
  }
  async askBrain() {
    this.busy = true;
    this.level.fx.worldUI.hide('bb' + this.center.x + ',' + this.center.z);
    const res = await this.level.startQuiz({ title: 'Brain Block!', sub: 'Solve it for bonus 💎 gems!', needed: 1, allowLater: true });
    this.busy = false;
    if (res.cancelled) return;
    this.breakOpen();
    const L = this.level;
    const gems = res.firstTry ? 3 : 1;
    for (let i = 0; i < gems; i++) L.spawnLoot(this.center, 'gem');
    for (let i = 0; i < 6; i++) L.spawnLoot(this.center, 'coin');
    L.fx.worldUI.float(res.firstTry ? '+3 💎 BRAIN BONUS!' : '+1 💎', this.center.clone().add(new THREE.Vector3(0, 1, 0)), 'ft-purple ft-big');
  }
  update(dt) {
    this.t += dt;
    if (Math.random() < dt * 2.5) this.level.fx.particles.sparkle(this.center.clone().add(new THREE.Vector3(0, 0.3, 0)), this.brainBlock ? '#e0c0ff' : '#fff2a0', 1);
    // gentle "notice me" pulse
    const s = 1 + Math.max(0, Math.sin(this.t * 3)) * 0.04;
    this.obj.scale.setScalar(s);
    if (this.brainBlock && !this.busy) {
      // just a hint bubble — Sai opens it by hitting it, like a Lucky Block
      const p = this.level.player.pos;
      const id = 'bb' + this.center.x + ',' + this.center.z;
      if (Math.hypot(p.x - this.center.x, p.z - this.center.z) < 3.2 && Math.abs(p.y - this.center.y) < 2) this.level.fx.worldUI.anchor(id, '🧠 Hit me for bonus 💎!', this.center.clone().add(new THREE.Vector3(0, 1.3, 0)), 'prompt');
      else this.level.fx.worldUI.hide(id);
    }
  }
  dispose() {
    super.dispose();
    if (this.brainBlock) this.level.fx.worldUI.hide('bb' + this.center.x + ',' + this.center.z);
    const i = this.level.colliders.indexOf(this.col);
    if (i >= 0) this.level.colliders.splice(i, 1);
    const j = this.level.hittables.indexOf(this);
    if (j >= 0) this.level.hittables.splice(j, 1);
  }
}

// ------------------------------------------------------------------ spinner bar
export class Spinner extends Ent {
  constructor(level, spec) {
    super(level);
    const g = new THREE.Group();
    g.position.set(spec.x, spec.y, spec.z);
    const bar = new THREE.Mesh(atlasBox(spec.len, 0.4, 0.4, { top: 'neon_red', side: 'neon_red' }), getAtlasMaterial());
    bar.position.y = 0.5;
    bar.castShadow = true;
    g.add(bar);
    this.obj = this.add(g);
    this.len = spec.len;
    this.speed = spec.speed;
    this.angle = 0;
    this.cool = 0;
  }
  update(dt) {
    this.angle += this.speed * dt;
    this.obj.rotation.y = this.angle;
    this.cool -= dt;
    const pl = this.level.player;
    const lx = pl.pos.x - this.obj.position.x, lz = pl.pos.z - this.obj.position.z;
    const c = Math.cos(-this.angle), s = Math.sin(-this.angle);
    const rx = lx * c - lz * s, rz = lx * s + lz * c;
    const y0 = this.obj.position.y + 0.3, y1 = this.obj.position.y + 0.7;
    if (this.cool <= 0 && Math.abs(rx) < this.len / 2 + 0.3 && Math.abs(rz) < 0.5 && pl.pos.y < y1 && pl.pos.y + pl.h > y0) {
      // knock along the bar's direction of travel
      const dir = Math.sign(rx) || 1;
      const tx = -Math.sin(this.angle) * dir, tz = -Math.cos(this.angle) * dir;
      pl.knock(new THREE.Vector3(tx, 0, tz), 9, 7);
      audio.play('hit');
      this.cool = 0.6;
    }
  }
}

// ------------------------------------------------------------------ portal
const portalVert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`;
const portalFrag = `
uniform float time; uniform vec3 c1; uniform vec3 c2; varying vec2 vUv;
void main(){
  vec2 uv = floor(vUv * vec2(18.0, 24.0)) / vec2(18.0, 24.0);
  vec2 p = uv - 0.5; p.x *= 0.75;
  float r = length(p); float a = atan(p.y, p.x);
  float sw = sin(a * 3.0 + r * 22.0 - time * 4.5) * 0.5 + 0.5;
  float sw2 = sin(a * 5.0 - r * 14.0 + time * 2.0) * 0.5 + 0.5;
  vec3 col = mix(c1, c2, sw * 0.7 + sw2 * 0.3) * (1.9 - r * 1.6);
  col += vec3(1.2) * smoothstep(0.12, 0.0, r);
  gl_FragColor = vec4(col, 0.93);
}`;
export class Portal extends Ent {
  constructor(level, spec) {
    super(level);
    this.mat = new THREE.ShaderMaterial({
      vertexShader: portalVert,
      fragmentShader: portalFrag,
      uniforms: { time: { value: 0 }, c1: { value: new THREE.Color(spec.c1 || '#7a2aff') }, c2: { value: new THREE.Color(spec.c2 || '#20e8ff') } },
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(3, 4), this.mat);
    m.position.set(spec.x, spec.y + 2, spec.z);
    this.obj = this.add(m);
    this.center = m.position.clone();
    this.active = spec.active !== false;
    this.obj.visible = this.active;
    this.done = false;
  }
  open() {
    this.active = true;
    this.obj.visible = true;
    this.obj.scale.set(0.01, 0.01, 1);
    this.grow = 0;
    audio.play('portal');
  }
  update(dt) {
    this.mat.uniforms.time.value += dt;
    if (!this.active) return;
    if (this.grow !== undefined && this.grow < 1) {
      this.grow = Math.min(1, this.grow + dt * 1.5);
      const s = easeOutBack(this.grow);
      this.obj.scale.set(s, s, 1);
    }
    const P = this.level.fx.particles;
    if (Math.random() < dt * 30) {
      const a = Math.random() * Math.PI * 2;
      const r = 2.2;
      P.spawn({ x: this.center.x + Math.cos(a) * r, y: this.center.y + Math.sin(a) * r * 1.2, z: this.center.z, vx: -Math.cos(a) * 2.6, vy: -Math.sin(a) * 3, vz: 0, life: 0.8, size: 0.09, color: Math.random() < 0.5 ? '#c08aff' : '#7af6ff', gravity: 0, glow: true, bright: 2, drag: 0 });
    }
    const p = this.level.player.pos;
    if (!this.done && Math.abs(p.x - this.center.x) < 1.6 && Math.abs(p.z - this.center.z) < 0.8 && p.y > this.center.y - 2.3 && p.y < this.center.y + 1.5) {
      this.done = true;
      this.level.enterPortal(this);
    }
  }
}

// ------------------------------------------------------------------ treasure chest
export class Chest extends Ent {
  constructor(level, spec) {
    super(level);
    const g = new THREE.Group();
    g.position.set(spec.x, spec.y, spec.z);
    g.rotation.y = Math.PI;
    const mat = getAtlasMaterial();
    const base = new THREE.Mesh(atlasBox(0.9, 0.6, 0.9, { top: 'chest_top', side: 'chest_side', front: 'chest_front' }), mat);
    base.position.y = 0.3;
    base.castShadow = true;
    const lidPivot = new THREE.Group();
    lidPivot.position.set(0, 0.6, -0.45);
    const lid = new THREE.Mesh(atlasBox(0.9, 0.3, 0.9, { top: 'chest_top', side: 'chest_side', front: 'chest_side' }), mat);
    lid.position.set(0, 0.15, 0.45);
    lid.castShadow = true;
    lidPivot.add(lid);
    g.add(base, lidPivot);
    this.obj = this.add(g);
    this.lid = lidPivot;
    this.opened = false;
    this.t = 0;
    this.center = new THREE.Vector3(spec.x, spec.y + 0.6, spec.z);
    this.col = { min: new THREE.Vector3(spec.x - 0.45, spec.y, spec.z - 0.45), max: new THREE.Vector3(spec.x + 0.45, spec.y + 0.9, spec.z + 0.45), solid: true };
    level.colliders.push(this.col);
  }
  update(dt) {
    const p = this.level.player.pos;
    if (!this.opened) {
      if (Math.random() < dt * 3) this.level.fx.particles.sparkle(this.center, '#ffe080', 1);
      if (Math.hypot(p.x - this.center.x, p.z - this.center.z) < 2.4 && Math.abs(p.y - this.obj.position.y) < 2) {
        this.opened = true;
        audio.play('chest');
        this.level.onChest(this.center.clone());
      }
    } else if (this.t < 1) {
      this.t = Math.min(1, this.t + dt * 2);
      this.lid.rotation.x = -easeOutBack(this.t) * 1.9;
    }
  }
  dispose() {
    super.dispose();
    const i = this.level.colliders.indexOf(this.col);
    if (i >= 0) this.level.colliders.splice(i, 1);
  }
}

// ------------------------------------------------------------------ NPC
const npcSkins = {};
export class NPC extends Ent {
  constructor(level, spec, id) {
    super(level);
    this.id = 'npc' + id;
    if (!npcSkins[spec.kind]) npcSkins[spec.kind] = paintNPC(spec.kind);
    this.ch = new Character(npcSkins[spec.kind], { name: spec.kind === 'professor' ? 'Prof. Pixel' : spec.kind === 'builder' ? 'Builder Bob' : 'Villager', tagScale: 0.7 });
    if (this.ch.tag) this.ch.tag.material.depthTest = true;
    this.ch.root.position.set(spec.x, spec.y, spec.z);
    this.ch.root.rotation.y = spec.yaw ?? Math.PI;
    this.baseYaw = this.ch.root.rotation.y;
    this.obj = this.add(this.ch.root);
    // Course lines say {name}, so guides greet whoever is playing.
    this.text = withName(spec.text, escapeHtml(level.game.save.data.name));
    this.talking = false;
    this.wave = 0;
  }
  update(dt) {
    const L = this.level;
    const p = L.player.pos;
    const o = this.ch.root.position;
    const dx = p.x - o.x, dz = p.z - o.z;
    const d = Math.hypot(dx, dz);
    let look = null;
    if (L.guideNPC === this) {
      const target = Math.atan2(dx, dz);
      let diff = target - this.ch.root.rotation.y;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.ch.root.rotation.y += diff * Math.min(1, dt * 3);
      if (!this.talking) {
        this.talking = true;
        this.wave = 1.6;
        audio.play('pop');
      }
      L.fx.worldUI.anchor(this.id, this.text, _v.set(o.x, o.y + 2.55, o.z), 'bubble');
      look = { x: -0.1, y: 0 };
    } else if (this.talking) {
      this.talking = false;
      L.fx.worldUI.hide(this.id);
    }
    this.wave -= dt;
    this.ch.update(dt, { speed: 0, grounded: true, wave: this.wave > 0, look });
    if (this.ch.tag) this.ch.tag.visible = this.talking;
    // don't let the camera poke through the NPC's head
    const cam = L.game.camera.position;
    this.ch.root.visible = Math.hypot(cam.x - o.x, cam.y - (o.y + 1.2), cam.z - o.z) > 3.4;
  }
  dispose() {
    this.level.fx.worldUI.hide(this.id);
    this.level.group.remove(this.ch.root);
    this.ch.dispose();
  }
}

// ------------------------------------------------------------------ sign
export class Sign extends Ent {
  constructor(level, spec) {
    super(level);
    const g = new THREE.Group();
    g.position.set(spec.x, spec.y, spec.z);
    g.rotation.y = spec.yaw ?? 0;
    const mat = getAtlasMaterial();
    const post = new THREE.Mesh(atlasBox(0.14, 1.1, 0.14, { top: 'log_top', side: 'log_side' }), mat);
    post.position.y = 0.55;
    const board = new THREE.Mesh(atlasBox(1.5, 0.75, 0.1, { top: 'planks', side: 'planks' }), mat);
    board.position.y = 1.3;
    post.castShadow = board.castShadow = true;
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 128;
    const ctx = c.getContext('2d');
    ctx.font = '54px "Lilita One", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#3a2410';
    ctx.fillText(spec.text, 128, 68, 236);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Mesh(new THREE.PlaneGeometry(1.45, 0.72), new THREE.MeshLambertMaterial({ map: tex, transparent: true }));
    label.position.set(0, 1.3, -0.056);
    label.rotation.y = Math.PI;
    const label2 = label.clone();
    label2.position.z = 0.056;
    label2.rotation.y = 0;
    g.add(post, board, label, label2);
    this.obj = this.add(g);
  }
}
